import { createSlice } from '@reduxjs/toolkit';

// Multi-entity slice (mirrors store/pool/poolSlice.js, which combines pools+tenants) — the four
// colo admin screens (Plan 06 Task 3) share one loading/error surface per the app's existing
// convention.
const initialState = {
  sources: [],
  outletMappings: [],
  collectors: [],
  // The plaintext enrollment token is only ever present immediately after CreateCollector — the
  // API never returns it again, so the Collectors screen must render it from here exactly once
  // and the caller clears it (clearEnrollmentToken) once the admin dismisses the dialog.
  lastCreatedCollector: null,
  orgFeatures: null,
  // Foreman host↔device match diagnostic (colo/sources/{id}/foreman-status). Its own
  // loading/error surface, separate from the shared isLoading/error, because the Foreman screen
  // loads the source list AND this live probe independently and shouldn't cross-clobber their
  // spinners/errors.
  foremanStatus: null,
  foremanStatusLoading: false,
  foremanStatusError: null,
  isLoading: false,
  error: null
};

const coloSlice = createSlice({
  name: 'colo',
  initialState,
  reducers: {
    requestStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    requestFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    // Generic "clear loading, no list/entity mutation" success — used by operations whose result
    // doesn't belong in this slice's lists (e.g. testSourceThunk's test-result detail).
    requestSuccess: state => {
      state.isLoading = false;
    },

    // Sources
    fetchSourcesSuccess: (state, action) => {
      state.sources = action.payload;
      state.isLoading = false;
    },
    createSourceSuccess: (state, action) => {
      state.sources.push(action.payload);
      state.isLoading = false;
    },
    updateSourceSuccess: (state, action) => {
      state.sources = state.sources.map(source =>
        source.id === action.payload.id ? action.payload : source
      );
      state.isLoading = false;
    },

    // Outlet mappings
    // NOTE: fetchOutletMappingsSuccess is the only reducer that mutates outletMappings. Create/
    // Close return the mutation DTO (id/validFrom/validTo), a different shape than the list rows
    // (pduRef/outletNumber/unmapped/mappingId/mappingValidFrom) this array holds — merging one
    // into the other silently corrupted every list row's fields (blank Valid-From, always-
    // "Active" status, Close targeting `undefined`). The callers (AssignOutletModal,
    // OutletsOverview's close handler) re-fetch the list on success instead, so these two just
    // clear the loading flag.
    fetchOutletMappingsSuccess: (state, action) => {
      state.outletMappings = action.payload;
      state.isLoading = false;
    },
    createOutletMappingSuccess: state => {
      state.isLoading = false;
    },
    closeOutletMappingSuccess: state => {
      state.isLoading = false;
    },
    importOutletMappingsSuccess: state => {
      state.isLoading = false;
    },

    // Collectors
    fetchCollectorsSuccess: (state, action) => {
      state.collectors = action.payload;
      state.isLoading = false;
    },
    createCollectorSuccess: (state, action) => {
      state.collectors.push(action.payload.collector);
      state.lastCreatedCollector = action.payload;
      state.isLoading = false;
    },
    revokeCollectorSuccess: (state, action) => {
      state.collectors = state.collectors.map(collector =>
        collector.id === action.payload.id ? action.payload : collector
      );
      state.isLoading = false;
    },
    clearLastCreatedCollector: state => {
      state.lastCreatedCollector = null;
    },

    // Org feature flags (admin read/write — Flags screen)
    fetchOrgFeaturesSuccess: (state, action) => {
      state.orgFeatures = action.payload;
      state.isLoading = false;
    },
    updateOrgFeaturesSuccess: (state, action) => {
      state.orgFeatures = action.payload;
      state.isLoading = false;
    },

    // Foreman status diagnostic
    foremanStatusStart: state => {
      state.foremanStatusLoading = true;
      state.foremanStatusError = null;
    },
    foremanStatusSuccess: (state, action) => {
      state.foremanStatus = action.payload;
      state.foremanStatusLoading = false;
    },
    foremanStatusFailure: (state, action) => {
      state.foremanStatusLoading = false;
      state.foremanStatusError = action.payload;
      state.foremanStatus = null;
    },
    clearForemanStatus: state => {
      state.foremanStatus = null;
      state.foremanStatusError = null;
      state.foremanStatusLoading = false;
    }
  }
});

export const {
  requestStart,
  requestFailure,
  requestSuccess,
  fetchSourcesSuccess,
  createSourceSuccess,
  updateSourceSuccess,
  fetchOutletMappingsSuccess,
  createOutletMappingSuccess,
  closeOutletMappingSuccess,
  importOutletMappingsSuccess,
  fetchCollectorsSuccess,
  createCollectorSuccess,
  revokeCollectorSuccess,
  clearLastCreatedCollector,
  fetchOrgFeaturesSuccess,
  updateOrgFeaturesSuccess,
  foremanStatusStart,
  foremanStatusSuccess,
  foremanStatusFailure,
  clearForemanStatus
} = coloSlice.actions;

export default coloSlice.reducer;
